import { useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, PhoneOff, AlertTriangle, Siren, Loader2, ShieldCheck } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { formatDateTimeFr } from "@/lib/format";

const CHOICES = [
  {
    id: "ok",
    label: "Tout va bien",
    sub: "Clôt l'alerte immédiatement",
    Icon: CheckCircle2,
    bg: "#22C55E",
  },
  {
    id: "no_response",
    label: "Pas de réponse",
    sub: "On ne parvient pas à le/la joindre",
    Icon: PhoneOff,
    bg: "#94A3B8",
  },
  {
    id: "concerning",
    label: "Inquiétant",
    sub: "Situation douteuse, surveiller",
    Icon: AlertTriangle,
    bg: "#F59E0B",
  },
  {
    id: "escalate",
    label: "Passer en feu rouge",
    sub: "Escalade immédiate, équipe + contacts",
    Icon: Siren,
    bg: "#EF4444",
    needsConfirm: true,
  },
];

export default function DebriefBlock({ alert, user, onUpdated }) {
  const [sending, setSending] = useState(null); // id of in-flight choice
  const [notes, setNotes] = useState("");

  const debrief = alert.debrief;
  const isOwner = alert.agent_id === user?.id;
  const isSupervisor = (alert.supervisors || []).includes(user?.id);
  const isAdmin = user?.role === "admin";
  const canDebrief = isOwner || isSupervisor || isAdmin;

  // Read-only if debrief already filled
  if (debrief) {
    const choice = CHOICES.find((c) => c.id === debrief.status) || CHOICES[0];
    return (
      <div className="sa-card px-5 py-5" data-testid="debrief-block">
        <div className="flex items-center gap-2">
          <ShieldCheck size={14} className="text-slate-500" />
          <h2 className="text-[13px] font-semibold uppercase tracking-wider text-slate-500">
            Compte-rendu
          </h2>
        </div>

        <div
          className="mt-3 flex items-start gap-3 rounded-[14px] border border-slate-200 bg-[var(--sa-subtle)] px-4 py-3"
          data-testid="debrief-readonly"
        >
          <div
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-white"
            style={{ background: choice.bg }}
          >
            <choice.Icon size={18} />
          </div>
          <div className="flex min-w-0 flex-1 flex-col">
            <span className="text-[14px] font-semibold text-slate-900">
              {choice.label}
            </span>
            <span className="text-[11px] text-slate-500">
              par {debrief.by_name || "—"} · {formatDateTimeFr(debrief.at)}
            </span>
            {debrief.notes ? (
              <p className="mt-2 whitespace-pre-line text-[13px] text-slate-700">
                {debrief.notes}
              </p>
            ) : null}
          </div>
        </div>
      </div>
    );
  }

  if (!canDebrief) {
    return null;
  }

  const handleSubmit = async (choiceId) => {
    const choice = CHOICES.find((c) => c.id === choiceId);
    if (choice.needsConfirm) {
      const ok = window.confirm(
        "Confirmer le passage en FEU ROUGE ? L'équipe entière sera alertée en urgence."
      );
      if (!ok) return;
    }
    setSending(choiceId);
    try {
      const { data } = await api.post(`/alerts/${alert.id}/debrief`, {
        status: choiceId,
        notes: notes.trim() || null,
      });
      toast.success(
        choiceId === "ok"
          ? "Tout va bien — alerte clôturée."
          : choiceId === "escalate"
          ? "Escalade en feu rouge enregistrée."
          : "Compte-rendu enregistré."
      );
      onUpdated(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(null);
    }
  };

  return (
    <div className="sa-card px-5 py-5" data-testid="debrief-block">
      <div className="flex items-center gap-2">
        <ShieldCheck size={14} className="text-slate-500" />
        <h2 className="text-[13px] font-semibold uppercase tracking-wider text-slate-500">
          Compte-rendu
        </h2>
      </div>
      <p className="mt-1 text-[12.5px] text-slate-500 leading-snug">
        Comment s'est passé le rendez-vous ? Votre compte-rendu clôt l'alerte ou
        déclenche l'escalade.
      </p>

      <div className="mt-3 grid grid-cols-2 gap-2">
        {CHOICES.map((c) => {
          const busy = sending === c.id;
          return (
            <button
              key={c.id}
              type="button"
              onClick={() => handleSubmit(c.id)}
              disabled={sending !== null}
              data-testid={`debrief-${c.id}`}
              className="flex flex-col items-start gap-1 rounded-[14px] border border-slate-200 bg-white p-3 text-left transition hover:border-slate-300 hover:shadow-sm disabled:opacity-60"
            >
              <span
                className="flex h-8 w-8 items-center justify-center rounded-full text-white"
                style={{ background: c.bg }}
              >
                {busy ? <Loader2 size={14} className="animate-spin" /> : <c.Icon size={16} />}
              </span>
              <span className="text-[13.5px] font-semibold text-slate-900">
                {c.label}
              </span>
              <span className="text-[11px] text-slate-500 leading-tight">
                {c.sub}
              </span>
            </button>
          );
        })}
      </div>

      <div className="mt-3">
        <label className="sa-label" htmlFor="debrief-notes">
          Notes (optionnel)
        </label>
        <textarea
          id="debrief-notes"
          data-testid="debrief-notes"
          className="sa-input"
          style={{ height: 70, padding: "10px 14px", resize: "vertical" }}
          placeholder="Précisions éventuelles pour l'équipe…"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          maxLength={2000}
        />
      </div>
    </div>
  );
}
